/**
 * CareTeamMemberForm Component
 * Form for adding/editing care team members
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, User, Building, Phone, Mail, MapPin } from 'lucide-react';
import { useCareTeamMemberTypes, useCareTeamMember } from '@/features/care-team/hooks/useCareTeam';
import type {
  CareTeamMemberFormData,
  CreateCareTeamMemberRequest,
  CareTeamMemberCategory,
} from '@/features/care-team/types/careTeamTypes';

// ============================================================================
// Types
// ============================================================================

interface CareTeamMemberFormProps {
  familyId: string;
  memberId?: string | null;
  childId?: string | null;
  onSubmit: (data: CreateCareTeamMemberRequest) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

// ============================================================================
// Initial Form State
// ============================================================================

const initialFormData: CareTeamMemberFormData = {
  memberTypeId: '',
  childId: null,
  name: '',
  title: '',
  organization: '',
  email: '',
  phone: '',
  fax: '',
  address: '',
  specialty: '',
  npiNumber: '',
  licenseNumber: '',
  notes: '',
  isPrimary: false,
  startDate: '',
};

// ============================================================================
// Form Component
// ============================================================================

export function CareTeamMemberForm({
  familyId,
  memberId,
  childId,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: CareTeamMemberFormProps) {
  const [formData, setFormData] = useState<CareTeamMemberFormData>({
    ...initialFormData,
    childId: childId || null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch member types
  const { data: memberTypes, isLoading: isLoadingTypes } = useCareTeamMemberTypes();

  // Fetch existing member data if editing
  const { data: existingMember, isLoading: isLoadingMember } = useCareTeamMember(
    memberId || null
  );

  // Populate form when editing
  useEffect(() => {
    if (existingMember) {
      setFormData({
        memberTypeId: existingMember.memberTypeId,
        childId: existingMember.childId,
        name: existingMember.name,
        title: existingMember.title || '',
        organization: existingMember.organization || '',
        email: existingMember.email || '',
        phone: existingMember.phone || '',
        fax: existingMember.fax || '',
        address: existingMember.address || '',
        specialty: existingMember.specialty || '',
        npiNumber: existingMember.npiNumber || '',
        licenseNumber: existingMember.licenseNumber || '',
        notes: existingMember.notes || '',
        isPrimary: existingMember.isPrimary,
        startDate: existingMember.startDate || '',
      });
    }
  }, [existingMember]);

  // Group member types by category
  const memberTypesByCategory = memberTypes?.reduce((acc, type) => {
    if (!acc[type.category]) {
      acc[type.category] = [];
    }
    acc[type.category].push(type);
    return acc;
  }, {} as Record<CareTeamMemberCategory, typeof memberTypes>);

  const categoryLabels: Record<CareTeamMemberCategory, string> = {
    medical: 'Medical Providers',
    therapy: 'Therapists',
    education: 'Education',
    legal: 'Legal',
    other: 'Other',
  };

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.memberTypeId) {
      newErrors.memberTypeId = 'Provider type is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const submitData: CreateCareTeamMemberRequest = {
      memberTypeId: formData.memberTypeId,
      childId: formData.childId || undefined,
      name: formData.name,
      title: formData.title || undefined,
      organization: formData.organization || undefined,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      fax: formData.fax || undefined,
      address: formData.address || undefined,
      specialty: formData.specialty || undefined,
      npiNumber: formData.npiNumber || undefined,
      licenseNumber: formData.licenseNumber || undefined,
      notes: formData.notes || undefined,
      isPrimary: formData.isPrimary,
      startDate: formData.startDate || undefined,
    };

    await onSubmit(submitData);
  };

  const isLoading = isLoadingTypes || isLoadingMember;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-warm-gray/10">
        <h2 className="text-xl font-display font-semibold text-navy">
          {memberId ? 'Edit Care Team Member' : 'Add Care Team Member'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-warm-gray hover:text-navy hover:bg-cream rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sage" />
        </div>
      ) : (
        <>
          {/* Provider Type */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Provider Type <span className="text-red-500">*</span>
            </label>
            <select
              name="memberTypeId"
              value={formData.memberTypeId}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-cream border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
                errors.memberTypeId
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-transparent focus:border-sage'
              }`}
            >
              <option value="">Select provider type...</option>
              {memberTypesByCategory &&
                Object.entries(memberTypesByCategory).map(([category, types]) => (
                  <optgroup key={category} label={categoryLabels[category as CareTeamMemberCategory]}>
                    {types?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
            </select>
            {errors.memberTypeId && (
              <p className="mt-1 text-sm text-red-500">{errors.memberTypeId}</p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dr. Jane Smith"
                className={`w-full pl-10 pr-4 py-3 bg-cream border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
                  errors.name
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-transparent focus:border-sage'
                }`}
              />
            </div>
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Title & Organization */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Pediatric Neurologist"
                className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                Organization
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Children's Hospital"
                  className="w-full pl-10 pr-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-navy-light uppercase tracking-wide">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="w-full pl-10 pr-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="dr.smith@hospital.com"
                    className={`w-full pl-10 pr-4 py-3 bg-cream border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
                      errors.email
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-transparent focus:border-sage'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                Address
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-warm-gray" />
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Medical Center Drive, Suite 100&#10;City, State 12345"
                  rows={2}
                  className="w-full pl-10 pr-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors resize-none"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-navy-light uppercase tracking-wide">
              Additional Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Specialty
                </label>
                <input
                  type="text"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleChange}
                  placeholder="Cerebral Palsy, Movement Disorders"
                  className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  NPI Number
                </label>
                <input
                  type="text"
                  name="npiNumber"
                  value={formData.npiNumber}
                  onChange={handleChange}
                  placeholder="1234567890"
                  className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional notes about this provider..."
                rows={3}
                className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors resize-none"
              />
            </div>
          </div>

          {/* Primary Provider Toggle */}
          <div className="flex items-center gap-3 p-4 bg-cream rounded-lg">
            <input
              type="checkbox"
              id="isPrimary"
              name="isPrimary"
              checked={formData.isPrimary}
              onChange={handleChange}
              className="w-5 h-5 rounded border-warm-gray text-sage focus:ring-sage"
            />
            <label htmlFor="isPrimary" className="text-sm">
              <span className="font-medium text-navy">Primary Provider</span>
              <p className="text-warm-gray">
                Mark this provider as the primary contact for their specialty
              </p>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-warm-gray/10">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {memberId ? 'Save Changes' : 'Add Member'}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

export default CareTeamMemberForm;

